/**
 * AuthContext - Contexte global pour l'authentification
 *
 * Centralise toute la logique d'authentification :
 * - État isAuthenticated partagé globalement
 * - Login/Logout sans rechargement de page
 * - Sync contacts/places au login uniquement
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { syncContactsFromServer, clearContactsCache } from '../services/userContactsService';
import { syncPlacesFromServer, clearPlacesCache } from '../services/userPlacesService';
import { getModules, getModulesList } from '../services/modulesService';
import { clearPreferencesCache } from '../services/preferencesService';
import { storage, recheckStorage } from '../services/storageService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be/rest/cabaza';

const AuthContext = createContext(null);

/**
 * Parse WDDX response from ColdFusion (fallback for non-JSON responses)
 */
const parseWDDX = (wddxString) => {
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(wddxString, 'text/xml');

        const parseNode = (node) => {
            if (!node) return null;
            const nodeName = node.nodeName.toLowerCase();

            switch (nodeName) {
                case 'struct': {
                    const obj = {};
                    const entries = node.children;
                    for (let i = 0; i < entries.length; i++) {
                        const entry = entries[i];
                        if (entry.nodeName.toLowerCase() === 'entry') {
                            const name = entry.getAttribute('NAME') || entry.getAttribute('name');
                            const type = (entry.getAttribute('TYPE') || entry.getAttribute('type') || '').toLowerCase();
                            const childStruct = entry.querySelector('STRUCT, struct');
                            const childArray = entry.querySelector('ARRAY, array');

                            if (childStruct) {
                                obj[name] = parseNode(childStruct);
                            } else if (childArray) {
                                obj[name] = parseNode(childArray);
                            } else if (type === 'number') {
                                obj[name] = parseFloat(entry.textContent) || 0;
                            } else if (type === 'boolean') {
                                obj[name] = entry.textContent.toLowerCase() === 'true';
                            } else {
                                obj[name] = entry.textContent || '';
                            }
                        }
                    }
                    return obj;
                }
                case 'array': {
                    const arr = [];
                    for (let i = 0; i < node.children.length; i++) {
                        arr.push(parseNode(node.children[i]));
                    }
                    return arr;
                }
                case 'string':
                    return node.textContent || '';
                case 'number':
                    return parseFloat(node.textContent) || 0;
                case 'boolean':
                    return node.textContent.toLowerCase() === 'true';
                default:
                    return node.textContent;
            }
        };

        const structNode = xmlDoc.querySelector('STRUCT, struct');
        return structNode ? parseNode(structNode) : null;
    } catch (e) {
        console.error('WDDX parse error:', e);
        return null;
    }
};

/**
 * Normalize user data from API response
 * Handles camelCase, lowercase and UPPERCASE keys from ColdFusion
 */
const normalizeUserData = (userData) => {
    const firstName = userData.firstName || userData.FIRSTNAME || userData.firstname || '';
    const lastName = userData.lastName || userData.LASTNAME || userData.lastname || '';
    const email = userData.email || userData.EMAIL || '';
    const id = userData.id || userData.ID || '';
    const peopleUuid = userData.peopleUuid || userData.PEOPLEUUID || userData.peopleuuid || '';
    const storageKey = userData.storageKey || userData.STORAGEKEY || userData.storagekey || '';
    const role = userData.role || userData.ROLE || '';
    const profileTypeId = userData.profileTypeId || userData.PROFILETYPEID || userData.profiletypeid || null;

    let isAdmin = 0;
    if (userData.isAdmin !== undefined) isAdmin = userData.isAdmin;
    else if (userData.ISADMIN !== undefined) isAdmin = userData.ISADMIN;
    else if (userData.isadmin !== undefined) isAdmin = userData.isadmin;

    return {
        id,
        email,
        firstName,
        lastName,
        peopleUuid,
        storageKey,
        role,
        profileTypeId,
        isAdmin: isAdmin === 1 || isAdmin === '1' || isAdmin === true
    };
};

/**
 * Save user data to localStorage
 */
const saveUserToStorage = (user, token = null) => {
    recheckStorage();
    if (token) {
        storage.setItem('authToken', token);
        // Le code partage (packages/shared/src/services/apiClient.ts) lit le
        // jeton sous « cabaza_jwt_token » — une cle que personne n'ecrivait.
        // Les services qui passent par lui, dont events, partaient donc sans
        // Authorization et prendront un 401 des que les gardes sont actives.
        storage.setItem('cabaza_jwt_token', token);
    }
    storage.setItem('userEmail', user.email);
    storage.setItem('userName', `${user.firstName} ${user.lastName}`.trim());
    storage.setItem('userId', user.id.toString());
    storage.setItem('userUuid', user.peopleUuid);
    storage.setItem('userRole', user.role);
    const profileTypeId = user.profileTypeId || user.profiletypeid || user.PROFILETYPEID;
    if (profileTypeId) {
        storage.setItem('userProfileTypeId', profileTypeId.toString());
    }
    storage.setItem('cabaza_user', JSON.stringify(user));
};

/**
 * Clear only auth-related data from localStorage
 * Keep contacts, places, preferences in cache for next login
 */
const clearAuthFromStorage = () => {
    storage.removeItem('authToken');
    storage.removeItem('cabaza_jwt_token');
    storage.removeItem('cabaza_api_key');
    storage.removeItem('cabaza_user');
    storage.removeItem('userProfileTypeId');
    storage.removeItem('userId');
    storage.removeItem('userUuid');
    storage.removeItem('userEmail');
    storage.removeItem('userName');
    storage.removeItem('userRole');
    storage.removeItem('cabazaModulesList');
};

/**
 * Get current user from localStorage
 */
export const getStoredUser = () => {
    try {
        const userJson = storage.getItem('cabaza_user');
        return userJson ? JSON.parse(userJson) : null;
    } catch (e) {
        console.error('Error parsing cabaza_user:', e);
        return null;
    }
};

/**
 * Check if user is admin
 */
export const isAdmin = () => {
    const user = getStoredUser();
    return user?.isAdmin === true;
};

/**
 * AuthProvider - Wrapper component that provides auth context
 */
const isTokenValid = (token) => {
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp ?? payload.EXP;
        return exp && exp * 1000 > Date.now();
    } catch {
        return false;
    }
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        const token = storage.getItem('authToken');
        const user = getStoredUser();
        return !!(isTokenValid(token) && user?.id);
    });
    const [user, setUser] = useState(() => getStoredUser());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // isInitializing: true until initial data is loaded (modules, etc.)
    const [isInitializing, setIsInitializing] = useState(true);

    // Initialize app data on mount (for authenticated users)
    useEffect(() => {
        const initializeApp = async () => {
            const token = storage.getItem('authToken');
            const storedUser = getStoredUser();

            if (token && storedUser?.id) {
                try {
                    // Sync modules: fetch from API and update cache if count changed
                    const cachedModules = getModulesList();
                    const modulesFromApi = await getModules('fr');
                    if (modulesFromApi && modulesFromApi.length !== cachedModules.length) {
                        storage.setItem('cabazaModulesList', JSON.stringify(modulesFromApi));
                    } else if (!cachedModules.length && modulesFromApi?.length) {
                        storage.setItem('cabazaModulesList', JSON.stringify(modulesFromApi));
                    }
                } catch (err) {
                    console.error('[AUTH] Error loading initial data:', err);
                }
            }

            // Mark initialization complete
            setIsInitializing(false);
        };

        initializeApp();
    }, []);

    /**
     * Login with email and code
     */
    const login = useCallback(async (email, code) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/peoples/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password: code }),
            });

            const text = await response.text();

            if (!text || text.trim() === '') {
                throw new Error('Empty server response');
            }

            let data;
            if (text.trim().startsWith('<')) {
                data = parseWDDX(text);
                if (!data) throw new Error('Unable to parse WDDX response');
            } else {
                data = JSON.parse(text);
            }

            const successRaw = data.success ?? data.SUCCESS;
            const message = data.message || data.MESSAGE;

            // Strict success check: must be boolean true or string "true"
            const isSuccess = successRaw === true || successRaw === 'true';

            if (!isSuccess) {
                throw new Error(message || 'Invalid credentials');
            }

            const responseData = data.data || data.DATA || data;
            const token = responseData.token || responseData.TOKEN || data.token || data.TOKEN;
            const userData = responseData.user || responseData.USER || data.user || data.USER || responseData;

            // Validate that we have actual user data
            if (!userData || typeof userData !== 'object') {
                throw new Error('Invalid user data received');
            }

            const normalizedUser = normalizeUserData({ ...userData, email });

            // Strict validation: user must have a valid ID
            if (!normalizedUser.id) {
                throw new Error('User ID missing from response');
            }

            // Validate token exists
            if (!token) {
                throw new Error('Authentication token missing');
            }

            // Clear cached preferences from previous user
            clearPreferencesCache();

            saveUserToStorage(normalizedUser, token);

            // Update state
            setUser(normalizedUser);
            setIsAuthenticated(true);
            setLoading(false);

            // Emit login event for other contexts (theme, notifications, etc.)
            window.dispatchEvent(new CustomEvent('userLoggedIn'));

            // Sync contacts and places (async, don't block)
            const userId = normalizedUser.id;
            const peopleuuid = normalizedUser.peopleUuid;
            const storageKey = normalizedUser.storageKey;

            if (userId && peopleuuid && storageKey) {
                syncContactsFromServer(userId, peopleuuid, storageKey)
                    .catch(err => console.error('[AUTH] Error syncing contacts:', err));
                syncPlacesFromServer(userId, peopleuuid, storageKey)
                    .catch(err => console.error('[AUTH] Error syncing places:', err));
            }

            // Sync modules: only fetch from API if count differs from localStorage
            const syncModules = async () => {
                try {
                    const cachedModules = getModulesList();
                    const modulesFromApi = await getModules('fr');

                    if (modulesFromApi && modulesFromApi.length !== cachedModules.length) {
                        storage.setItem('cabazaModulesList', JSON.stringify(modulesFromApi));
                    } else if (!cachedModules.length && modulesFromApi?.length) {
                        storage.setItem('cabazaModulesList', JSON.stringify(modulesFromApi));
                    }
                } catch (err) {
                    console.error('[AUTH] Error syncing modules:', err);
                }
            };
            syncModules();

            return { success: true, user: normalizedUser };

        } catch (err) {
            console.error('Login error:', err);
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    }, []);

    /**
     * Login with OAuth (after redirect from Google/Apple)
     */
    const loginWithOAuth = useCallback(async (authToken, authEmail) => {
        setLoading(true);
        setError(null);

        try {
            storage.setItem('authToken', authToken);
            // Meme raison qu'au login classique : le code partage lit cette cle.
            storage.setItem('cabaza_jwt_token', authToken);
            storage.setItem('userEmail', authEmail);

            const response = await fetch(`${API_BASE_URL}/peoples?email=${encodeURIComponent(authEmail)}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();
            const success = result.success || result.SUCCESS;
            const data = result.data || result.DATA || [];

            if (success && data.length > 0) {
                const userData = data[0];
                const normalizedUser = normalizeUserData({ ...userData, email: authEmail });

                // Clear cached preferences (in case of user switch)
                clearPreferencesCache();

                saveUserToStorage(normalizedUser, authToken);

                setUser(normalizedUser);
                setIsAuthenticated(true);
                setLoading(false);

                // Emit login event for other contexts (theme, notifications, etc.)
                window.dispatchEvent(new CustomEvent('userLoggedIn'));

                // Sync contacts and places
                const userId = normalizedUser.id;
                const peopleuuid = normalizedUser.peopleUuid;
                const storageKey = normalizedUser.storageKey;

                if (userId && peopleuuid && storageKey) {
                    syncContactsFromServer(userId, peopleuuid, storageKey).catch(console.error);
                    syncPlacesFromServer(userId, peopleuuid, storageKey).catch(console.error);
                }

                // Sync modules: only fetch from API if count differs from localStorage
                const syncModules = async () => {
                    try {
                        const cachedModules = getModulesList();
                        const modulesFromApi = await getModules('fr');

                        if (modulesFromApi && modulesFromApi.length !== cachedModules.length) {
                            storage.setItem('cabazaModulesList', JSON.stringify(modulesFromApi));
                        } else if (!cachedModules.length && modulesFromApi?.length) {
                            storage.setItem('cabazaModulesList', JSON.stringify(modulesFromApi));
                        }
                    } catch (err) {
                        console.error('[AUTH] Error syncing modules:', err);
                    }
                };
                syncModules();

                return { success: true, user: normalizedUser };
            } else {
                throw new Error('User not found');
            }

        } catch (err) {
            console.error('OAuth login error:', err);
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    }, []);

    /**
     * Logout - simple, no sync needed
     */
    const logout = useCallback(() => {
        clearAuthFromStorage();
        clearPreferencesCache();
        clearContactsCache();
        clearPlacesCache();
        setUser(null);
        setIsAuthenticated(false);
        window.dispatchEvent(new CustomEvent('userLoggedOut'));
    }, []);

    /**
     * Refresh user data from API
     */
    const refreshUser = useCallback(async () => {
        const currentUser = getStoredUser();
        if (!currentUser?.peopleUuid) {
            return { success: false, error: 'No user logged in' };
        }

        try {
            const token = storage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/peoples/${currentUser.peopleUuid}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();
            const success = result.success || result.SUCCESS;
            const data = result.data || result.DATA;

            if (success && data) {
                const normalizedUser = normalizeUserData({ ...data, email: currentUser.email });
                saveUserToStorage(normalizedUser);
                setUser(normalizedUser);
                return { success: true, user: normalizedUser };
            } else {
                throw new Error(result.message || result.MESSAGE || 'Failed to refresh user');
            }

        } catch (err) {
            console.error('Refresh user error:', err);
            return { success: false, error: err.message };
        }
    }, []);

    const value = {
        isAuthenticated,
        user,
        loading,
        error,
        setError,
        isInitializing,
        login,
        loginWithOAuth,
        logout,
        refreshUser,
        getUser: () => user || getStoredUser(),
        isAdmin: () => user?.isAdmin === true || isAdmin(),
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Hook to use auth context
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
